import React, { useEffect, useRef } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { BridgeClient } from '../../app/service/bridge/BridgeClient';
import { useShellBootstrap } from '../../features/shell/bootstrap/useShellBootstrap';
import { setStartMode } from '../../util/appUtils';
import {
  saveShellSessionFromContext,
  clearShellSession,
} from '../../features/shell/session/shellSessionStorage';
import { shellLogger, generateFlowId } from '../../features/shell/logging/shellLogger';
import { shellIncidentReporter } from '../../features/shell/logging/shellIncidentReporter';
import LoadingPage from './LoadingPage';
import IntlMessages from 'util/IntlMessages';

/**
 * ShellBootstrapEntry — production shell entry point for the embedded iframe path.
 *
 * Mounted at /embedded/app.
 *
 * Flow:
 *   1. Sets start mode to 'iframe' so RestrictedRoute allows /app without a
 *      legacy session token.
 *   2. Creates a BridgeClient and runs the shell bootstrap sequence.
 *   3. Routes based on the resulting bootstrap context status:
 *      - isLoading               → LoadingPage (spinner)
 *      - ready + routeShopId     → saves session, Redirect to returnTo (if safe)
 *                                  or /app/:routeShopId/home
 *      - ready + no routeShopId  → degraded recovery UI
 *      - needs_auth_recovery     → Redirect to /embedded/register
 *      - degraded                → inline recovery UI with Retry button
 *
 * Silent rebootstrap path:
 *   ShellSessionGate and ShellAppContextProvider redirect here with
 *   `?returnTo=/app/:shopId/...` when the shell session is missing or expired.
 *   On successful bootstrap the operator returns to their intended route
 *   automatically, with no manual-retry step required.
 */
const ShellBootstrapEntry = () => {
  const location = useLocation();
  // Read the returnTo parameter. Validation (shop binding) happens below once
  // the bootstrapped routeShopId is known, so we just decode here.
  const rawReturnTo = new URLSearchParams(location.search).get('returnTo') || null;
  // Observability: one flowId per component lifetime (covers full bootstrap + wizard path).
  const flowIdRef = useRef(null);
  if (!flowIdRef.current) flowIdRef.current = generateFlowId();
  // Record wall-clock start for bootstrap durationMs calculation.
  const startMsRef = useRef(Date.now());

  // Set iframe start mode once on mount so the RestrictedRoute gate passes.
  useEffect(() => {
    setStartMode('iframe');
    shellLogger.debug('bootstrap.start', { flowId: flowIdRef.current });
  }, []);

  // Stable bridge reference — created once per mount.
  const bridgeRef = useRef(null);
  if (!bridgeRef.current) {
    bridgeRef.current = new BridgeClient();
  }

  const { isLoading, context, reload } = useShellBootstrap(bridgeRef.current);

  // Log routing decisions once per bootstrap outcome.
  useEffect(() => {
    if (isLoading) return;

    const durationMs = Date.now() - startMsRef.current;
    const flowId = flowIdRef.current;

    if (!context) {
      shellLogger.warn('bootstrap.route', {
        flowId,
        route: null,
        reason: 'no_context',
        status: 'degraded',
        durationMs,
      });
      shellIncidentReporter.report('bootstrap.degraded', {
        flowId,
        shopId: null,
        route: null,
        stage: 'bootstrap',
        errorCode: 'NO_CONTEXT',
        status: 'degraded',
        durationMs,
      });
      return;
    }

    if (context.status === 'needs_auth_recovery') {
      shellLogger.info('bootstrap.route', {
        flowId,
        shopId: context.routeShopId || null,
        route: '/embedded/register',
        reason: 'needs_auth_recovery',
        status: context.status,
        durationMs,
      });
      shellIncidentReporter.report('bootstrap.auth_recovery', {
        flowId,
        shopId: context.routeShopId || null,
        route: '/embedded/register',
        stage: 'bootstrap',
        errorCode: 'AUTH_RECOVERY',
        status: 'needs_auth_recovery',
        durationMs,
      });
      return;
    }

    if (context.status === 'ready' && context.routeShopId) {
      const wizardDone = context.onboardingWizardCompleted;
      // Reflect actual routing decision in telemetry (mirrors the render branch above).
      const shopId = encodeURIComponent(context.routeShopId);
      const shopPrefixRaw = `/app/${context.routeShopId}/`;
      const shopPrefixEnc = `/app/${shopId}/`;
      const safeReturnTo = rawReturnTo && (
        rawReturnTo.startsWith(shopPrefixRaw) || rawReturnTo.startsWith(shopPrefixEnc)
      ) ? rawReturnTo : null;
      let loggedRoute;
      if (wizardDone === false) {
        loggedRoute = `/app/${context.routeShopId}/setup`;
      } else if (safeReturnTo) {
        loggedRoute = safeReturnTo;
      } else {
        loggedRoute = `/app/${context.routeShopId}/home`;
      }
      const reason = wizardDone === false
        ? 'wizard_not_completed'
        : (safeReturnTo ? 'silent_rebootstrap_return' : 'wizard_completed_or_unknown');
      shellLogger.info('bootstrap.route', {
        flowId,
        shopId: context.routeShopId,
        route: loggedRoute,
        reason,
        status: 'ready',
        durationMs,
      });
      return;
    }

    shellLogger.warn('bootstrap.route', {
      flowId,
      route: null,
      reason: 'degraded_or_missing_shop_id',
      status: context.status || 'unknown',
      durationMs,
    });
    shellIncidentReporter.report('bootstrap.degraded', {
      flowId,
      shopId: context.routeShopId || null,
      route: null,
      stage: 'bootstrap',
      errorCode: 'DEGRADED_OR_MISSING_SHOP_ID',
      status: context.status || 'unknown',
      durationMs,
    });
  }, [isLoading, context]);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (context && context.status === 'needs_auth_recovery') {
    clearShellSession();
    const recoveryCode = context.session?.error?.code || 'AUTH_RECOVERY';
    return <Redirect to={`/embedded/register?code=${encodeURIComponent(recoveryCode)}`} />;
  }

  if (context && context.status === 'ready' && context.routeShopId) {
    saveShellSessionFromContext({ ...context, flowId: flowIdRef.current });
    const shopId = encodeURIComponent(context.routeShopId);
    // Bind returnTo to the bootstrapped shopId to prevent cross-shop shell state:
    // only accept paths whose second segment matches the bootstrapped shop id.
    // e.g. /app/shop-42/... is safe; /app/other-shop/... is rejected → /home.
    const safeReturnTo = (() => {
      if (!rawReturnTo) return null;
      // Must start with /app/<bootstrappedShopId>/ (encoded or raw match).
      const shopPrefix = `/app/${context.routeShopId}/`;
      const shopPrefixEncoded = `/app/${shopId}/`;
      return (rawReturnTo.startsWith(shopPrefix) || rawReturnTo.startsWith(shopPrefixEncoded))
        ? rawReturnTo
        : null;
    })();
    // Wizard explicitly incomplete → always send to setup, even if returnTo is set.
    // safeReturnTo present and wizard done (or unknown) → honour the return route.
    // Otherwise default to /home.
    let targetPath;
    if (context.onboardingWizardCompleted === false) {
      targetPath = `/app/${shopId}/setup`;
    } else if (safeReturnTo) {
      targetPath = safeReturnTo;
    } else {
      targetPath = `/app/${shopId}/home`;
    }
    return <Redirect to={targetPath} />;
  }

  // Degraded: status='degraded', or ready but routeShopId is absent.
  clearShellSession();
  return (
    <div style={styles.root}>
      <div style={styles.box}>
        <p style={styles.message}>
          <IntlMessages id="ui.embedded.shellBootstrapEntry.unableToConnectToThePluginPleaseCheckYourConfiguration" />
        </p>
        {context && context.session && context.session.error && (
          <p style={styles.detail}>
            {context.session.error.message || context.session.error.code}
          </p>
        )}
        <button
          type="button"
          style={styles.button}
          onClick={reload}
          data-testid="shell-retry-button"
        >
          <IntlMessages id="ui.embedded.shellBootstrapEntry.retry" />
        </button>
      </div>
    </div>
  );
};

export default ShellBootstrapEntry;

const styles = {
  root: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100vw',
    minHeight: '100vh',
    padding: 24,
    boxSizing: 'border-box',
    background: '#fff',
    fontFamily: 'sans-serif',
  },
  box: {
    maxWidth: 420,
    padding: 32,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    textAlign: 'center',
  },
  message: {
    fontSize: 15,
    color: '#333',
    marginBottom: 12,
  },
  detail: {
    fontSize: 12,
    color: '#888',
    marginBottom: 12,
    wordBreak: 'break-all',
  },
  button: {
    padding: '8px 24px',
    fontSize: 14,
    cursor: 'pointer',
    border: '1px solid #1976d2',
    borderRadius: 4,
    background: '#1976d2',
    color: '#fff',
  },
};
